import React, { KeyboardEvent } from 'react';
import { getKeys } from '../../../../../../_helpers/keyboard';
import { Menu } from '../../../../../../components/blackrock/advisor-center/_types/navigation';
import HeaderMenuItem from '../header-menu-item/HeaderMenuItem';

import './HeaderPrimaryNavigation.scss';

export type HeaderPrimaryNavigationProps = {
	menu: Menu[];
	isMobile: boolean;
	isOpen: boolean;
	isExpanded: boolean;
	selectedMenuIndex: number;
	setExpanded: React.Dispatch<boolean>;
	header: React.MutableRefObject<HTMLDivElement>;
	primaryNavItemIndex: number;
	setPrimaryNavItemIndex: React.Dispatch<number>;
	setSecondaryNavItemIndex: React.Dispatch<number>;
	onMenuItemClick?: (menuItem: Menu) => void;
};

const HeaderPrimaryNavigation: React.FC<HeaderPrimaryNavigationProps> = ({
	menu,
	isMobile,
	isOpen,
	isExpanded,
	selectedMenuIndex,
	setExpanded,
	header,
	primaryNavItemIndex,
	setPrimaryNavItemIndex,
	setSecondaryNavItemIndex,
	onMenuItemClick,
}) => {
	const shouldSetAriaHidden = (menuItem: Menu) => {
		const isMobileItem =
			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			// @ts-ignore
			menuItem.additionalStyleClass.includes('blk-ac-only-mobile');

		return (isMobileItem && !isMobile) || (isMobile && !isOpen);
	};

	const expandMenuItem = (index: number): void => {
		const hasChildren = !!menu[index]?.menuGroups?.length;

		if (isMobile || !hasChildren) {
			return;
		}

		setPrimaryNavItemIndex(index);
		setExpanded(true);
	};

	const contractMenuItem = () => {
		if (isMobile) return;

		setExpanded(false);
	};

	const onClickMenuItem = (menuIndex: number, menuItem: Menu) => {
		onMenuItemClick?.(menuItem);

		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		const hasChildren = !!menu[menuIndex]?.menuGroups.length;

		setPrimaryNavItemIndex(menuIndex);

		if (!hasChildren) return;

		setExpanded(true);
	};

	const onKeyDown = (event: KeyboardEvent, index: number) => {
		const secondaryContainer = header.current.querySelector(
			'.blk-ac-header-secondary-navigation'
		);
		const secondaryVisibleWrapper =
			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			// @ts-ignore
			secondaryContainer.querySelectorAll('.wrapper')[primaryNavItemIndex];
		const secondaryVisibleLinks =
			secondaryVisibleWrapper.querySelectorAll('.secondary a');
		const primaryContainer = header.current.querySelector(
			'.blk-ac-header-primary-navigation'
		);
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		const primaryVisibleLinks = primaryContainer.querySelectorAll('.primary a');
		const prevLinkHasChildren =
			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			// @ts-ignore
			!!menu[primaryNavItemIndex - 1]?.menuGroups.length;
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		const hasChildren = !!menu[index]?.menuGroups.length;
		const targetIsFirst = index === 0;
		const keys = getKeys(event.nativeEvent);

		if (keys.isDown && !hasChildren) {
			event.stopPropagation();
			event.preventDefault();

			const newPrimaryIndex = primaryNavItemIndex + 1;
			const link = primaryVisibleLinks[newPrimaryIndex] as HTMLElement;

			link.focus();
			setPrimaryNavItemIndex(newPrimaryIndex);
		}

		if ((keys.isDown || keys.isEnter || keys.isSpace) && hasChildren) {
			event.stopPropagation();
			event.preventDefault();

			const link = secondaryVisibleLinks[0] as HTMLElement;

			link.focus();
			setSecondaryNavItemIndex(0);
			setExpanded(true);
		}

		if (!prevLinkHasChildren && !targetIsFirst && keys.isUp) {
			event.stopPropagation();
			event.preventDefault();

			const newPrimaryIndex = primaryNavItemIndex - 1;
			const link = primaryVisibleLinks[newPrimaryIndex] as HTMLElement;

			link.focus();
			setPrimaryNavItemIndex(newPrimaryIndex);
		}

		if (prevLinkHasChildren && !targetIsFirst && keys.isUp) {
			event.stopPropagation();
			event.preventDefault();

			const newPrimaryIndex = primaryNavItemIndex - 1;
			const secondaryVisibleWrapper =
				// eslint-disable-next-line @typescript-eslint/ban-ts-comment
				// @ts-ignore
				secondaryContainer.querySelectorAll('.wrapper')[newPrimaryIndex];
			const secondaryVisibleLinks =
				secondaryVisibleWrapper.querySelectorAll('.secondary a');
			const link = secondaryVisibleLinks[
				secondaryVisibleLinks.length - 1
			] as HTMLElement;

			setPrimaryNavItemIndex(newPrimaryIndex);
			setSecondaryNavItemIndex(secondaryVisibleLinks.length - 1);
			setExpanded(true);
			link.focus();

			setTimeout(() => {
				link.focus();
			}, 0);
		}
	};

	return (
		<span className="blk-ac-header-primary-navigation front-of-bg">
			<div id="navigation-drawer">
				<ul>
					{menu.map((menuItem, index) => (
						<li
							// eslint-disable-next-line @typescript-eslint/ban-ts-comment
							// @ts-ignore
							key={menuItem.href + index}
							{...(shouldSetAriaHidden(menuItem) && {
								'aria-hidden': true,
							})}
							onMouseEnter={() => expandMenuItem(index)}
							onMouseLeave={() => contractMenuItem()}
						>
							<HeaderMenuItem
								className={[
									'primary',
									'blk-ac-body-m',
									menuItem.additionalStyleClass,
									menuItem.active ? 'active' : '',
									index === selectedMenuIndex &&
									isExpanded &&
									// eslint-disable-next-line @typescript-eslint/ban-ts-comment
									// @ts-ignore
									!!menuItem.menuGroups.length
										? 'hovered'
										: '',
								].join(' ')}
								onFocus={() => setPrimaryNavItemIndex(index)}
								onKeydown={(e) => onKeyDown(e, index)}
								// eslint-disable-next-line @typescript-eslint/ban-ts-comment
								// @ts-ignore
								label={menuItem.name}
								icon={menuItem.icon}
								href={menuItem.href}
								// eslint-disable-next-line @typescript-eslint/ban-ts-comment
								// @ts-ignore
								hasChild={!!menuItem.menuGroups.length}
								// eslint-disable-next-line @typescript-eslint/ban-ts-comment
								// @ts-ignore
								target={menuItem.target}
								// eslint-disable-next-line @typescript-eslint/ban-ts-comment
								// @ts-ignore
								ariaLabel={menuItem.ariaLabel}
								// eslint-disable-next-line @typescript-eslint/ban-ts-comment
								// @ts-ignore
								dataLinkEvent={menuItem.dataLinkEvent}
								tabIndex={shouldSetAriaHidden(menuItem) ? -1 : 0}
								titleValue={''}
								isExpanded={isExpanded}
								onClick={() => onClickMenuItem(index, menuItem)}
							></HeaderMenuItem>
						</li>
					))}
				</ul>
			</div>
		</span>
	);
};

export default HeaderPrimaryNavigation;
